import React, {useEffect, useRef, useState} from "react";
import EditorJS from "@editorjs/editorjs";
import {editorConfig} from "./editorConfig";
import {News} from "../../models/News";
import PhotoUploadBlock from "./PhotoUploadBlock";
import {BASE_URLS} from "../../config/urls";
import {apiFetch} from "../../utils/api";
import {BtnSpinner} from "../../components/fields/BtnSpinner";

interface NewsCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    newsItem?: News; // если передали — режим редактирования
}

export const NewsCreateModal: React.FC<NewsCreateModalProps> = ({ isOpen, onClose, newsItem }) => {
    const [title, setTitle] = useState(newsItem?.title || "");
    const [photo, setPhoto] = useState<File | null>(null);
    const [photoUrl, setPhotoUrl] = useState<string>(newsItem?.photoUrl || "");
    const [shortDescription, setShortDescription] = useState(newsItem?.shortDescription || "");
    const [mediaId, setMediaId] = useState<number | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const holderRef = useRef<HTMLDivElement | null>(null);
    const editorRef = useRef<EditorJS | null>(null);

    // ESC — закрыть (и не закрываем, если идёт сохранение)
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (isSubmitting) return;
                onClose();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onClose, isSubmitting]);

    // при открытии/смене newsItem — пересоздаём editor с нужными данными
    useEffect(() => {
        if (!isOpen) return;
        if (!holderRef.current) return;

        // если редактор уже был — уничтожаем и создаём заново (иначе данные не обновятся)
        const destroy = async () => {
            try {
                await editorRef.current?.destroy?.();
            } catch (_) {}
            editorRef.current = null;
        };

        const init = async () => {
            await destroy();

            const data =
                newsItem?.description && newsItem.description !== ""
                    ? JSON.parse(newsItem.description)
                    : { blocks: [] };

            editorRef.current = new EditorJS(editorConfig(holderRef.current!, data));
        };

        init();

        // cleanup при закрытии
        return () => {
            destroy();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, newsItem?.id]);

    // когда открываем модалку под другой newsItem — обновим поля
    useEffect(() => {
        if (!isOpen) return;
        setTitle(newsItem?.title || "");
        setPhoto(null);
        setPhotoUrl(newsItem?.photoUrl || "");
        setShortDescription(newsItem?.shortDescription || "");
        setMediaId(null);
        setIsSubmitting(false);
    }, [isOpen, newsItem]);

    const MAX_UPLOAD_MB = 10;
    const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

    const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isSubmitting) return;
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];

        if (file.size > MAX_UPLOAD_BYTES) {
            alert(`⚠️ Файл слишком большой. Максимум ${MAX_UPLOAD_MB} MB.`);
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await apiFetch(`${BASE_URLS.api}/upload-photo`, {
                method: "POST",
                body: formData,
            });

            if (res.status === 413) {
                alert("⚠️ Файл слишком большой. Пожалуйста, выберите изображение меньшего размера.");
                return;
            }

            if (!res.ok) throw new Error(`Ошибка загрузки фото (код ${res.status})`);

            const data = await res.json();
            setMediaId(data.id);
            setPhoto(file);
            setPhotoUrl(""); // если загрузили новое — url старой фотки убираем
        } catch (err) {
            console.error(err);
            alert("Не удалось загрузить фото. Проверьте соединение или попробуйте позже.");
        }
    };

    const handleSave = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            if (!editorRef.current) return;

            const savedData = await editorRef.current.save();

            if (!title) {
                alert("Заголовок не заполнен");
                return;
            }
            if (!shortDescription) {
                alert("Краткое описание не заполнено");
                return;
            }
            if (!mediaId && !photoUrl) {
                alert("Фото не прикреплено");
                return;
            }

            const id = newsItem?.id ?? 0;

            const body = JSON.stringify({
                id,
                title,
                shortDescription,
                description: JSON.stringify(savedData),
                mediaId, // если не загружали заново — будет null (и бэк должен оставить старое фото по photoUrl)
            });

            const response = await apiFetch(`${BASE_URLS.api}/news/save`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body,
            });

            if (!response.ok) throw new Error(`Ошибка при сохранении (${response.status})`);

            // как у тебя было
            window.location.assign(`${BASE_URLS.app}/admin/news`);
        } catch (err) {
            console.error(err);
            alert("Ошибка при сохранении новости");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const isSaveDisabled = isSubmitting;

    return (
        <div
            className="profile-modal-overlay"
            onMouseDown={() => {
                if (isSubmitting) return;
                onClose();
            }}
        >
            <div className="profile-modal profile-modal--news" onMouseDown={(e) => e.stopPropagation()}>
                <div className="profile-modal-head">
                    <div>
                        <div className="profile-modal-title">
                            {newsItem ? "Редактировать новость" : "Создать новость"}
                        </div>
                        <div className="profile-modal-subtitle">Заполните поля и нажмите “Сохранить”</div>
                    </div>

                    <button
                        className="profile-modal-close"
                        onClick={() => {
                            if (isSubmitting) return;
                            onClose();
                        }}
                        aria-label="Закрыть"
                        disabled={isSubmitting}
                        style={isSubmitting ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                    >
                        ✕
                    </button>
                </div>

                <div className="profile-modal-body">
                    <div className="news-popup" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="text"
                            placeholder="Заголовок"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="news-popup__input"
                            disabled={isSubmitting}
                        />

                        <PhotoUploadBlock
                            photo={photo}
                            photoUrl={photoUrl}
                            handlePhotoChange={handlePhotoChange}
                            // если в PhotoUploadBlock есть input, он сам будет вызывать handlePhotoChange,
                            // но на всякий случай можно прокинуть disabled, если компонент поддерживает:
                            // disabled={isSubmitting}
                        />

                        <textarea
                            placeholder="Краткое описание"
                            value={shortDescription}
                            onChange={(e) => setShortDescription(e.target.value)}
                            className="news-popup__textarea"
                            disabled={isSubmitting}
                        />

                        <small className="news-popup__commands">
                            <span>Быстрые команды для вставки:</span>
                            <ul>
                                <li>CTRL+SHIFT+H - заголовок</li>
                                <li>CTRL+SHIFT+P - абзац</li>
                                <li>CTRL+SHIFT+I - изображение</li>
                                <li>CTRL+SHIFT+L - список</li>
                            </ul>
                            Начинать необходимо с новой строки. Дополнительно доступны настройки при выделении текста.
                        </small>

                        <div
                            ref={holderRef}
                            className="news-popup__editor"
                            style={isSubmitting ? { pointerEvents: "none", opacity: 0.7 } : undefined}
                        />

                        <div className="news-popup__footer">
                            <button
                                type="button"
                                className="admin-button-secondary admin-button-secondary--ghost"
                                onClick={onClose}
                                disabled={isSubmitting}
                                style={isSubmitting ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                            >
                                Отмена
                            </button>
                            <button
                                type="submit"
                                className="admin-button-primary"
                                onClick={handleSave}
                                disabled={isSaveDisabled}
                                style={isSaveDisabled ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                            >
                                {isSubmitting ? <BtnSpinner/> : "Сохранить"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
